import React, { useEffect, useRef } from 'react';

const BAR_COUNT = 56;

export default function AudioWaveform({ stream }) {
  const bars = useRef([]);
  useEffect(() => {
    bars.current.forEach(bar => { bar?.setAttribute('height', 4); bar?.setAttribute('y', 48); });
    if (!stream) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    let context, source, frame;
    const dispose = () => {
      cancelAnimationFrame(frame);
      source?.disconnect();
      if (context && context.state !== 'closed') context.close().catch(() => {});
    };
    try {
      context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      source = context.createMediaStreamSource(stream);
      source.connect(analyser); // Never connect to destination: avoid microphone feedback.
      context.resume().catch(() => {});
      const samples = new Uint8Array(analyser.fftSize);
      const history = Array(BAR_COUNT).fill(4);
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      let last = 0;
      const draw = time => {
        if (!document.hidden && time - last >= (reducedMotion ? 125 : 50)) {
          last = time;
          analyser.getByteTimeDomainData(samples);
          const power = samples.reduce((sum, sample) => sum + ((sample - 128) / 128) ** 2, 0) / samples.length;
          const amplitude = Math.min(1, Math.sqrt(power) * 4);
          history.shift();
          history.push(4 + Math.pow(amplitude, 0.75) * 88);
          history.forEach((height, index) => {
            bars.current[index]?.setAttribute('height', height.toFixed(2));
            bars.current[index]?.setAttribute('y', ((100 - height) / 2).toFixed(2));
          });
        }
        frame = requestAnimationFrame(draw);
      };
      frame = requestAnimationFrame(draw);
    } catch { dispose(); }
    return dispose;
  }, [stream]);

  return <svg className="studio-recorder__waveform" viewBox="0 0 448 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
    {Array.from({ length: BAR_COUNT }, (_, index) => <rect key={index} ref={element => { bars.current[index] = element; }} x={index * 8 + 2} y="48" width="4" height="4" rx="2" fill="currentColor" opacity={0.25 + (index / (BAR_COUNT - 1)) * 0.75} />)}
  </svg>;
}
